import moment from "moment-timezone"
import { BOOKING_STATUS } from "@/constants/schedule.js"
import {
  getBookingAvailabilities,
  updateCoachBookingAvailabilities,
  getCoachBookedSessions,
} from "@/api/user/coaching/schedule"
import {
  dismissMissedSession,
  getBookedSessionById,
  getCoachMissedSessions,
  initBookedSession,
} from "@/api/coach/schedule"

const state = () => ({
  availableTimeSlots: [],
  selectedDates: [],
  editing: false,
  error: "",
  bookedSessions: [],
  missedSessions: [],
  pendingBooking: null,
  studentAbsentSession: null,
  initiatedRequestId: "",
})

const getters = {
  availableTimeSlots: state => state.availableTimeSlots,
  selectedDates: state => state.selectedDates,
  editing: state => state.editing,
  timeslotsToday: state => {
    const parsedDate = moment(state.selectedDates[0]).format("MMM D")
    return state.availableTimeSlots.filter(
      slot =>
        moment
          .utc(slot.period_start)
          .local()
          .format("MMM D") === parsedDate && !slot.delete
    )
  },
  error: state => state.error,
  bookedSessions: state => state.bookedSessions,
  bookedSessionsToday: state => {
    const parsedDate = moment(state.selectedDates[0]).format("YYYY-MM-DD")
    return state.bookedSessions.filter(session => {
      return (
        moment
          .utc(session.session_start)
          .local()
          .format("YYYY-MM-DD") === parsedDate
      )
    })
  },
  missedSessions: state => state.missedSessions,
  pendingBooking: state => state.pendingBooking,
  studentAbsentSession: state => state.studentAbsentSession,
  initiatedRequestId: state => state.initiatedRequestId,
}

const mutations = {
  setAvailableTimeSlots: (state, availableTimeSlots) =>
    (state.availableTimeSlots = availableTimeSlots),
  setSelectedDates: (state, selectedDates) =>
    (state.selectedDates = [...selectedDates]),
  setEditing: (state, editing) => (state.editing = editing),
  setError: (state, error) => (state.error = error),
  setBookedSessions: (state, bookedSessions) =>
    (state.bookedSessions = bookedSessions),
  setMissedSessions: (state, sessions) => (state.missedSessions = sessions),
  setPendingBooking: (state, booking) => (state.pendingBooking = booking),
  setStudentAbsentSession: (state, studentAbsentSession) =>
    (state.studentAbsentSession = studentAbsentSession),
  setInitiatedRequestId: (state, initiatedRequestId) =>
    (state.initiatedRequestId = initiatedRequestId),
}

const actions = {
  async fetchCoachTimeSlots ({ commit, dispatch }, period) {
    const instaProAccessToken = await dispatch(
      "User/Coaching/loadInstaProAccessToken",
      null,
      {
        root: true,
      }
    )
    const from = moment
      .utc()
      .set({
        year: period.year,
        month: period.month - 1,
        date: 1,
      })
      .add(-2, "day")
      .format("YYYY-MM-DD")
    const to = moment
      .utc()
      .set({
        year: period.year,
        month: period.month,
        date: 2,
      })
      .format("YYYY-MM-DD")
    const data = await getBookingAvailabilities(instaProAccessToken, {
      from,
      to,
    })
    if (data.success) {
      commit("setAvailableTimeSlots", data.result)
    }
  },
  toggleEditing ({ commit, state }) {
    commit("setEditing", !state.editing)
  },
  async updateCoachBookingAvailabilities ({ commit, dispatch, state }) {
    commit("setError", "")
    if (!state.selectedDates.length) {
      return
    }
    const instaProAccessToken = await dispatch(
      "User/Coaching/loadInstaProAccessToken",
      null,
      {
        root: true,
      }
    )
    if (state.selectedDates.length <= 1) {
      const body = {
        availabilities: state.availableTimeSlots,
      }
      const data = await updateCoachBookingAvailabilities(
        instaProAccessToken,
        body
      )
      if (!data.success) {
        commit("setError", data.message)
      } else {
        const page = {
          month: moment.utc(state.selectedDates[0]).format("M"),
          year: moment.utc(state.selectedDates[0]).year(),
        }
        await dispatch("fetchCoachTimeSlots", page)
      }
    }
  },
  async getCoachBookedSessions ({ commit, dispatch }, date) {
    const instaProAccessToken = await dispatch(
      "User/Coaching/loadInstaProAccessToken",
      null,
      {
        root: true,
      }
    )
    let from = date
    from.setDate(1)
    from.setHours(0, 0, 0, 0)
    from = moment.utc(from).format()
    const data = await getCoachBookedSessions(instaProAccessToken, from)
    await commit("setBookedSessions", data.result)
  },
  async getPendingBooking ({ commit, dispatch }) {
    const instaProAccessToken = await dispatch(
      "User/Coaching/loadInstaProAccessToken",
      null,
      {
        root: true,
      }
    )
    let from = moment
      .utc()
      .add(-2, "day")
      .format()
    let to = moment
      .utc()
      .add(2, "day")
      .format()
    const data = await getCoachBookedSessions(instaProAccessToken, from, to)
    if (data.success) {
      const booking = data.result.find(
        booking => booking.status === BOOKING_STATUS.CHECKED_IN
      )
      if (booking) {
        commit("setPendingBooking", { ...booking })
      } else {
        const booking = data.result.find(
          booking => booking.status === BOOKING_STATUS.PENDING_CHECK_IN
        )
        if (booking) {
          commit("setPendingBooking", { ...booking })
        }
      }
    }
  },
  async getCoachMissedSessions ({ commit, dispatch }) {
    const instaProAccessToken = await dispatch(
      "User/Coaching/loadInstaProAccessToken",
      null,
      {
        root: true,
      }
    )
    const data = await getCoachMissedSessions(instaProAccessToken)
    if (data.success) {
      commit("setMissedSessions", data.result)
    }
  },
  async dismissMissedSession ({ dispatch, state }) {
    const instaProAccessToken = await dispatch(
      "User/Coaching/loadInstaProAccessToken",
      null,
      {
        root: true,
      }
    )
    const missedSession = state.missedSessions[0]
    await dismissMissedSession(instaProAccessToken, missedSession.id)
  },
  async initBookedSession ({ state, dispatch, commit }) {
    const instaProAccessToken = await dispatch(
      "User/Coaching/loadInstaProAccessToken",
      null,
      {
        root: true,
      }
    )
    initBookedSession(instaProAccessToken, state.pendingBooking.id)
    commit("setInitiatedRequestId", state.pendingBooking.request_id)
  },
  async getBookedSessionById ({ commit, dispatch }, { bookingId, setter }) {
    const instaProAccessToken = await dispatch(
      "User/Coaching/loadInstaProAccessToken",
      null,
      {
        root: true,
      }
    )
    const data = await getBookedSessionById(instaProAccessToken, bookingId)
    if (data.success) {
      commit(setter, { ...data.result })
    }
  },
}
export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}



// WEBPACK FOOTER //
// ./src/store/modules/user/coaching/schedule.js